import { useState } from "react";
import { useBootstrapCsrf, useRegister } from "../api/api_hooks";
import { Link, useNavigate } from "react-router-dom";
import { AuthError, AuthField, AuthIcon, AuthLayout, AuthSubmitButton } from "../components/AuthLayout";
import { useRemotePage } from "../remote/RemoteControlContext";

function registrationError(error) {
  const data = error?.response?.data;
  if (!data) return error?.message || "We couldn't create your account. Please try again.";

  const firstMessage = Object.values(data).flat().find((value) => typeof value === "string");
  return firstMessage || data.detail || "We couldn't create your account. Please check your details.";
}

export default function RegisterPage() {
  useBootstrapCsrf();
  const nav = useNavigate();
  const register = useRegister();

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password1, setPassword1] = useState("");
  const [password2, setPassword2] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);
  const [formError, setFormError] = useState("");

  useRemotePage({
    id: "register",
    title: "Create account",
    subtitle: "Enter account details securely on the main screen",
    state: {},
    sections: [],
    actions: {},
  });

  const onSubmit = (event) => {
    event.preventDefault();
    if (password1 !== password2) {
      setFormError("Those passwords don't match yet. Check them and try again.");
      return;
    }

    setFormError("");
    register.mutate(
      { username, email, password1, password2 },
      { onSuccess: () => nav("/login") }
    );
  };

  const passwordToggle = (
    <button
      type="button"
      onClick={() => setShowPasswords((visible) => !visible)}
      className="absolute inset-y-0 right-0 grid w-12 place-items-center text-indigo-200/35 transition hover:text-white focus-visible:outline-none focus-visible:text-cyan-200"
      aria-label={showPasswords ? "Hide passwords" : "Show passwords"}
    >
      <AuthIcon name={showPasswords ? "eye-off" : "eye"} />
    </button>
  );

  return (
    <AuthLayout mode="register">
      <header>
        <p className="text-xs font-black uppercase tracking-[0.3em] text-pink-200/70">Player one, step in</p>
        <h1 className="mt-3 text-3xl font-black tracking-[-0.025em] text-white sm:text-4xl">Create your player profile.</h1>
        <p className="mt-3 text-sm leading-6 text-indigo-100/50">One account keeps your games, wins, and tournament nights together.</p>
      </header>

      <form onSubmit={onSubmit} className="mt-8 grid gap-5 sm:grid-cols-2">
        <AuthField
          icon="user"
          label="Username"
          name="username"
          type="text"
          autoComplete="username"
          placeholder="player_one"
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          required
        />
        <AuthField
          icon="mail"
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
        <AuthField
          icon="lock"
          label="Password"
          hint="8+ characters"
          name="password"
          type={showPasswords ? "text" : "password"}
          autoComplete="new-password"
          placeholder="Create a password"
          value={password1}
          onChange={(event) => setPassword1(event.target.value)}
          required
          minLength={8}
          action={passwordToggle}
        />
        <AuthField
          icon="lock"
          label="Confirm password"
          name="password-confirmation"
          type={showPasswords ? "text" : "password"}
          autoComplete="new-password"
          placeholder="Repeat your password"
          value={password2}
          onChange={(event) => setPassword2(event.target.value)}
          required
          minLength={8}
        />

        <div className="sm:col-span-2">
          {(formError || register.isError) && (
            <AuthError>{formError || registrationError(register.error)}</AuthError>
          )}
        </div>

        <div className="sm:col-span-2">
          <AuthSubmitButton disabled={register.isPending}>
            {register.isPending ? "Creating your profile..." : "Create my account"}
          </AuthSubmitButton>
        </div>
      </form>

      <p className="mt-7 text-center text-sm text-indigo-100/45">
        Already have a profile?{" "}
        <Link to="/login" className="font-bold text-cyan-200 transition hover:text-cyan-100">Sign in here</Link>
      </p>
    </AuthLayout>
  );
}
